import { __t } from '@/lib/helpers'
import { store } from '@/store/index.js'
import {
  ACCESS_LEVELS,
  PAY_BY_APP_STATUSES,
  FEATURE_FLAGS,
} from '@/lib/consts.ts'
import { ROUTE_META_PARAMS } from '@/router/consts.ts'
import { shouldBeVisible as shouldBeVisibleByCertification } from '@/router/routes/business/sales.ts'
import { resolveBooksyPayEntryRedirect, BOOKSY_PAY_ENTRY_TARGETS } from '@/composables/payments/booksy-pay/entry.ts'

const bcrAvailable = (store) => {
  return store.getters['business/bcrAvailable']
}

const shouldBeVisible = (store) => {
  const { identity } = store.state
  if (!identity.user) {
    return false
  }
  if ([ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.OWNER, ACCESS_LEVELS.RECEPTION, ACCESS_LEVELS.ADVANCED].includes(identity.user.access_level)) {
    return true
  }
  return store.getters['business/isBcrConnectAvailable']
}

const hasKYCPassed = store => {
  const { pay_by_app_status: payByAppStatus } = store.state.business.pos

  return payByAppStatus === PAY_BY_APP_STATUSES.ENABLED
}

const beforeEnterBooksyPayOnboarding = async (to, _from, next) => {
  const redirectRoute = await resolveBooksyPayEntryRedirect({
    targetView: BOOKSY_PAY_ENTRY_TARGETS.ONBOARDING,
    store,
    route: to,
  })

  if (redirectRoute) {
    return next(redirectRoute)
  }

  next()
}

export default {
  path: 'payments',
  alias: 'payments/payment-types',
  name: 'settings.payments',
  meta: {
    title: __t('routes.settings.payments.title'),
    subtitle: __t('routes.settings.payments.subtitle'),
    icon: 'customer-payments',
    backgroundIcon: 'card-payments',
    [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE]: shouldBeVisible,
    useSmartlook: true,
  },
  component: () => import('@/views/business/settings/payments/index.vue'),
  children: [
    {
      path: 'mobile-payments',
      name: 'settings.payments.mobile-payments',
      meta: {
        title: __t('routes.settings.payments.mobile-payments.title'),
        subtitleGetter: store => hasKYCPassed(store)
          ? __t('routes.settings.payments.mobile-payments.subtitle')
          : __t('routes.settings.payments.mobile-payments.subtitle-no-kyc'),
        [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
        [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE] (store) {
          const { routingConfig, business } = store.state
          return business.pos.force_stripe_pba && routingConfig.config.pos__pay_by_app
        },
      },
      component: () => import('@/components/shared/b-pass-through.vue'),
      children: [
        {
          path: 'mobile-payments-success',
          name: 'settings.payments.payment-types.mobile-payments-success',
          meta: {
            title: '',
            subtitle: '',
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
          },
          component: () => import('@/views/business/settings/payments/payment-types/mobile-payments/success.vue'),
        },
        {
          path: 'mobile-payments-pending',
          name: 'settings.payments.payment-types.mobile-payments-pending',
          meta: {
            title: '',
            subtitle: '',
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
          },
          component: () => import('@/views/business/settings/payments/payment-types/mobile-payments/pending.vue'),
        },
        {
          path: 'mobile-payments-restricted',
          name: 'settings.payments.payment-types.mobile-payments-restricted',
          meta: {
            title: '',
            subtitle: '',
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
          },
          component: () => import('@/views/business/settings/payments/payment-types/mobile-payments/restricted.vue'),
        },
      ],
    },
    {
      path: 'booksy-gift-cards',
      name: 'settings.payments.booksy-gift-cards',
      meta: {
        title: __t('routes.settings.payments.booksy-gift-cards.title'),
        subtitle: __t('routes.settings.payments.booksy-gift-cards.subtitle'),
        hintKey: 'hide',
        [ROUTE_META_PARAMS.HIDDEN_IN_SEARCH]: true,
        [ROUTE_META_PARAMS.FEATURE_FLAG_EPPO]: FEATURE_FLAGS.Feature_BgcPaymentAndCheckoutTile,
        [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE]: (store) => {
          return store.getters['routingConfig/isBooksyGiftCardsEnabled']
        },
      },
    },
    {
      path: 'booksy-pay',
      name: 'settings.payments.booksy-pay',
      meta: {
        fullWidth: true,
        title: __t('routes.settings.payments.booksy-pay.title'),
        subtitleGetter: store => store.state.business.booksyPaySettings?.description || __t('routes.settings.payments.booksy-pay.subtitle'),
        hintKey: 'hide',
        [ROUTE_META_PARAMS.HIDDEN_IN_SEARCH]: true,
        [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE]: (store) => {
          return store.getters['business/shouldUseBooksyPay']
        },
        [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.OWNER,
      },
      component: () => import('@/views/business/settings/payments/booksy-pay/index.vue'),
      children: [
        {
          path: 'onboarding',
          name: 'settings.payments.booksy-pay.onboarding',
          beforeEnter: beforeEnterBooksyPayOnboarding,
          component: () => import('@/views/business/settings/payments/booksy-pay/onboarding/index.vue'),
        },
      ],
    },
    {
      path: 'no-show-protection',
      name: 'settings.payments-no-show-protection',
      meta: {
        title: __t('routes.settings.no-show.title'),
        subtitleGetter: store => hasKYCPassed(store)
          ? __t('routes.settings.no-show.subtitle')
          : __t('routes.settings.no-show.subtitle-no-kyc'),
        [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
        notifications: true,
        [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE] (store) {
          const { routingConfig, business } = store.state
          return business.pos.force_stripe_pba && routingConfig.config.pos__pay_by_app
        },
        hintKey: 'hintFeatureNoShow',
      },
      component: () => import('@/views/business/settings/no-show-protection/index.vue'),
    },
    {
      path: 'transactions',
      name: 'settings.payments.transactions',
      meta: {
        title: __t('components.payments.transactions.title'),
        subtitle: __t('components.payments.transactions.subtitle'),
        [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.ADVANCED,
        [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE]: shouldBeVisibleByCertification,
        defaultSubroute: {
          name: 'settings.payments.transactions.list',
        },
      },
      component: () => import('@/views/business/settings/payments/transactions/index.vue'),
      children: [
        {
          path: ':transactionId/edit',
          name: 'settings.payments.transactions.edit',
          meta: {
            title: '',
            subtitle: '',
            fullWidth: true,
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
          },
          component: () => import('@/views/business/settings/payments/transactions/form.vue'),
        },
        {
          path: ':transactionId',
          name: 'settings.payments.transactions.details',
          meta: {
            title: '',
            subtitle: '',
          },
          component: () => import('@/views/business/settings/payments/transactions/list.vue'),
        },
        {
          path: '',
          name: 'settings.payments.transactions.list',
          meta: {
            [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE]: shouldBeVisibleByCertification,
            title: '',
            subtitle: '',
          },
          component: () => import('@/views/business/settings/payments/transactions/list.vue'),
        },
      ],
    },
    {
      path: 'your-account',
      name: 'settings.payments.your-account',
      meta: {
        title: __t('routes.settings.payments.your-account.title'),
        subtitle: __t('routes.settings.payments.your-account.subtitle'),
        [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE] (store) {
          const { routingConfig, business } = store.state
          return business.pos.force_stripe_pba && routingConfig.config.pos__pay_by_app
        },
        hintKey: 'hintFeatureKYC',
        [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
      },
      component: () => import('@/views/business/settings/payments/your-account.vue'),
    },
    {
      path: 'booksy-card-reader',
      name: 'settings.payments.payment-types.booksy-card-reader',
      meta: {
        title: __t('components.payments.stripe.booksy-card-reader'),
        subtitle: __t('components.payments.stripe.tile.subtitle'),
        hintKey: 'hide',
        [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE]: (store) => {
          return bcrAvailable(store) && shouldBeVisible(store)
        },
      },
      component: () => import('@/views/business/settings/payments/payment-types/booksy-card-reader/index.vue'),
      children: [
        {
          path: 'verify-account',
          name: 'settings.payments.payment-types.booksy-card-reader.verify-account',
          meta: {
            title: '',
            subtitle: '',
            hintKey: 'hide',
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
          },
          component: () => import('@/views/business/settings/payments/payment-types/booksy-card-reader/verify-account.vue'),
        },
        {
          path: 'verify-account-redirect',
          name: 'settings.payments.payment-types.booksy-card-reader.verify-account-redirect',
          meta: {
            title: '',
            subtitle: '',
            hintKey: 'hide',
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
          },
          component: () => import('@/views/business/settings/payments/payment-types/booksy-card-reader/verify-account-redirect.vue'),
        },
        {
          path: 'verification-status-info',
          name: 'settings.payments.payment-types.booksy-card-reader.verification-status-info',
          meta: {
            title: '',
            subtitle: '',
            hintKey: 'hide',
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
          },
          component: () => import('@/views/business/settings/payments/payment-types/booksy-card-reader/verification-status-info.vue'),
        },
        {
          path: 'blocked',
          name: 'settings.payments.payment-types.booksy-card-reader.blocked',
          meta: {
            title: '',
            subtitle: '',
            hintKey: 'hide',
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.MANAGER,
          },
          component: () => import('@/views/business/settings/payments/payment-types/booksy-card-reader/blocked.vue'),
        },
        {
          path: 'connect-new-reader',
          name: 'settings.payments.payment-types.booksy-card-reader.connect-new-reader',
          meta: {
            title: '',
            subtitle: '',
            hintKey: 'hide',
          },
          component: () => import('@/views/business/settings/payments/payment-types/booksy-card-reader/connect-new-reader.vue'),
        },
        {
          path: 'connect',
          name: 'settings.payments.payment-types.booksy-card-reader.connect',
          meta: {
            title: '',
            subtitle: '',
            hintKey: 'hide',
          },
          component: () => import('@/views/business/settings/payments/payment-types/booksy-card-reader/connect.vue'),
        },
        {
          path: 'fast-payouts',
          name: 'settings.payments.payment-types.booksy-card-reader.fast-payouts',
          meta: {
            title: '',
            subtitle: '',
            hintKey: 'hide',
            [ROUTE_META_PARAMS.ACCESS_LEVEL]: ACCESS_LEVELS.OWNER,
          },
          component: () => import('@/views/business/settings/payments/payment-types/booksy-card-reader/fast-payouts.vue'),
        },
      ],
    },
    {
      path: 'tap-to-pay',
      name: 'settings.payments.tap-to-pay',
      meta: {
        title: __t('routes.settings.payments.tap-to-pay.title'),
        subtitle: __t('routes.settings.payments.tap-to-pay.subtitle'),
        hintKey: 'hide',
        [ROUTE_META_PARAMS.SHOULD_BE_VISIBLE]: (store) => {
          return store.getters['business/shouldUseTapToPay'] && store.getters['routingConfig/shouldUseTapToPay']
        },
      },
    },
    {
      path: 'checkout-settings',
      name: 'settings.payments.checkout-settings',
      meta: {
        title: __t('routes.settings.payments.checkout-settings.title'),
        subtitle: __t('routes.settings.payments.checkout-settings.subtitle'),
        hintKey: 'hintFeaturePosRetailSettings',
      },
      component: () => import('@/views/business/settings/payments/checkout-settings/index.vue'),
    },
  ],
}
